import { useMemo, useState } from "react";

export interface InspectionTemplateOption {
  id: string;
  name: string;
  serviceId: string | null;
  serviceName: string | null;
  phase: string;
  items: Array<{
    id: string;
    label: string;
    itemType: "pass_fail" | "numeric" | "text" | "photo_required" | "select";
    options: string[];
    isRequired: boolean;
  }>;
}

export interface InspectionBookingOption {
  id: string;
  serviceId: string;
  serviceName: string;
  customerName: string;
  startAt: string;
  status: string;
}

export interface UploadedInspectionMedia {
  id: string;
  caption: string | null;
  phase: string;
  mediaType: string;
  fileUrl: string;
}

interface InspectionExecutionProps {
  templates: InspectionTemplateOption[];
  bookings: InspectionBookingOption[];
  uploadingItemId: string | null;
  executing: boolean;
  onUploadPhoto(bookingId: string, itemId: string, file: File): Promise<UploadedInspectionMedia>;
  onExecute(input: {
    templateId: string;
    bookingId: string;
    overallResult: "pass" | "fail" | "conditional";
    notes: string;
    responses: Array<{
      templateItemId: string;
      value: string | null;
      notes: string | null;
      flagged: boolean;
      mediaId: string | null;
    }>;
  }): Promise<void>;
}

type ResponseState = {
  value: string;
  notes: string;
  flagged: boolean;
  media: UploadedInspectionMedia | null;
};

const PASS_FAIL_OPTIONS = [
  { value: "pass", label: "Pass" },
  { value: "fail", label: "Fail" },
  { value: "na", label: "N.A." },
];

export function InspectionExecution({
  templates,
  bookings,
  uploadingItemId,
  executing,
  onUploadPhoto,
  onExecute,
}: InspectionExecutionProps) {
  const [templateId, setTemplateId] = useState("");
  const [bookingId, setBookingId] = useState("");
  const [overallResult, setOverallResult] = useState<"pass" | "fail" | "conditional">("pass");
  const [notes, setNotes] = useState("");
  const [responses, setResponses] = useState<Record<string, ResponseState>>({});

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === templateId) ?? null,
    [templateId, templates],
  );

  const visibleBookings = useMemo(() => {
    if (!selectedTemplate?.serviceId) return bookings;
    return bookings.filter((booking) => booking.serviceId === selectedTemplate.serviceId);
  }, [bookings, selectedTemplate]);

  function ensureResponse(itemId: string): ResponseState {
    return responses[itemId] ?? {
      value: "",
      notes: "",
      flagged: false,
      media: null,
    };
  }

  function updateResponse(itemId: string, next: Partial<ResponseState>) {
    setResponses((current) => ({
      ...current,
      [itemId]: {
        ...ensureResponse(itemId),
        ...next,
      },
    }));
  }

  async function handleUpload(itemId: string, fileList: FileList | null) {
    if (!bookingId || !fileList?.[0]) return;
    const media = await onUploadPhoto(bookingId, itemId, fileList[0]);
    updateResponse(itemId, { media });
  }

  const canSubmit = Boolean(selectedTemplate && bookingId);

  return (
    <div className="space-y-6 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-primary)]">Inspection execution</p>
        <h2 className="mt-2 text-xl font-semibold text-[var(--color-foreground)]">Run checklist on a live booking</h2>
        <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
          Execute the template against a real booking, upload dedicated inspection evidence, and persist the result on the booking inspection seam.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Template</span>
          <select
            value={templateId}
            onChange={(event) => {
              setTemplateId(event.target.value);
              setBookingId("");
              setResponses({});
            }}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
          >
            <option value="">Select template</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>{template.name}</option>
            ))}
          </select>
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Booking</span>
          <select
            value={bookingId}
            onChange={(event) => setBookingId(event.target.value)}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
          >
            <option value="">Select booking</option>
            {visibleBookings.map((booking) => (
              <option key={booking.id} value={booking.id}>
                {booking.customerName} · {booking.serviceName}
              </option>
            ))}
          </select>
        </label>
      </div>

      {selectedTemplate && (
        <div className="space-y-4">
          {selectedTemplate.items.map((item) => {
            const response = ensureResponse(item.id);
            return (
              <div key={item.id} className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--color-foreground)]">{item.label}</p>
                    <p className="text-xs text-[var(--color-muted-foreground)]">{item.itemType.replaceAll("_", " ")}</p>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-medium text-[var(--color-foreground)]">
                    <input
                      type="checkbox"
                      checked={response.flagged}
                      onChange={(event) => updateResponse(item.id, { flagged: event.target.checked })}
                    />
                    Flag for follow-up
                  </label>
                </div>

                {item.itemType === "pass_fail" && (
                  <select
                    value={response.value}
                    onChange={(event) => updateResponse(item.id, { value: event.target.value })}
                    className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                  >
                    <option value="">Select result</option>
                    {PASS_FAIL_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                )}

                {item.itemType === "numeric" && (
                  <input
                    type="number"
                    value={response.value}
                    onChange={(event) => updateResponse(item.id, { value: event.target.value })}
                    className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                  />
                )}

                {item.itemType === "text" && (
                  <textarea
                    rows={3}
                    value={response.value}
                    onChange={(event) => updateResponse(item.id, { value: event.target.value })}
                    className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                  />
                )}

                {item.itemType === "select" && (
                  <select
                    value={response.value}
                    onChange={(event) => updateResponse(item.id, { value: event.target.value })}
                    className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                  >
                    <option value="">Select option</option>
                    {item.options.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                )}

                {item.itemType === "photo_required" && (
                  <div className="space-y-3">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(event) => void handleUpload(item.id, event.target.files)}
                    />
                    {uploadingItemId === item.id && (
                      <p className="text-xs text-[var(--color-muted-foreground)]">Uploading photo...</p>
                    )}
                    {response.media && (
                      <div className="rounded-xl border border-[var(--color-border)] p-3">
                        <p className="text-sm font-medium text-[var(--color-foreground)]">Photo attached</p>
                        <img src={response.media.fileUrl} alt={item.label} className="mt-3 max-h-48 rounded-lg border border-[var(--color-border)] object-cover" />
                      </div>
                    )}
                  </div>
                )}

                <textarea
                  rows={2}
                  value={response.notes}
                  onChange={(event) => updateResponse(item.id, { notes: event.target.value })}
                  placeholder="Notes for this checklist item"
                  className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                />
              </div>
            );
          })}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Overall result</span>
          <select
            value={overallResult}
            onChange={(event) => setOverallResult(event.target.value as "pass" | "fail" | "conditional")}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
          >
            <option value="pass">Pass</option>
            <option value="fail">Fail</option>
            <option value="conditional">Conditional</option>
          </select>
        </label>
        <label className="space-y-1 text-sm">
          <span className="text-xs font-medium uppercase text-[var(--color-muted-foreground)]">Inspection notes</span>
          <textarea
            rows={3}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
          />
        </label>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          disabled={!canSubmit || executing}
          onClick={() => {
            if (!selectedTemplate || !bookingId) return;
            void onExecute({
              templateId: selectedTemplate.id,
              bookingId,
              overallResult,
              notes,
              responses: selectedTemplate.items.map((item) => {
                const response = ensureResponse(item.id);
                return {
                  templateItemId: item.id,
                  value: response.value || null,
                  notes: response.notes || null,
                  flagged: response.flagged,
                  mediaId: response.media?.id ?? null,
                };
              }),
            });
          }}
          className="rounded-xl bg-[var(--color-primary)] px-4 py-3 text-sm font-semibold text-[var(--color-primary-foreground)] disabled:opacity-60"
        >
          {executing ? "Saving inspection..." : "Save inspection"}
        </button>
      </div>
    </div>
  );
}
